import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import LayoutProvider from '../../../Contexts/LayoutProvider';
import LibModal from '../../../features/LibModal/LibModal';
import { useTemplate } from '../../../features/template/TemplateProvider';
import PageTitle from '../../../utils/PageTitle';
import { mkRelativeUrl } from '../../../utils/URL';
import AgencyProvider, { useAgency } from '../AgencyProvider';
import AgencyPageHeader from './Components/AgencyPageHeader';
import AgencyPageHeaderMobile from './Components/AgencyPageHeaderMobile';
import AgencyPageTableMobile from './Components/AgencyPageTableMobile';
import AgencyPageTableWeb from './Components/AgencyPageTableWeb';
import ModalAgencyChangeCurrency from './Modals/ModalAgencyChangeCurrency';
import ModalAgencyInfoUpdate from './Modals/ModalAgencyInfoUpdate';
import ModalChangeCustomerCurrency from './Modals/ModalChangeCustomerCurrency';
import ModalCreateAgency from './Modals/ModalCreateAgency';
import ModalCreateAgencyCustomer from './Modals/ModalCreateAgencyCustomer';
import ModalCustomerMoveAgency from './Modals/ModalCustomerMoveAgency';

function PageCont() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [queryStr, setQueryStr] = useState({ q: searchParams.get('q') || '' });
    const { setParams, agencyInfo, handleReload } = useAgency();
    const { modalType, closeModal } = useTemplate();
    // Search Params
    const p = useMemo(
        () => ({
            status: searchParams.get('status') || '',
            q: searchParams.get('q') || '',
            'order-by': searchParams.get('order-by'),
            sort: searchParams.get('sort'),
            page: searchParams.get('page')
        }),
        [searchParams]
    );
    // Debounce Search Handler
    const debounceUpdate = useDebouncedCallback((val) => {
        navigate(mkRelativeUrl('/agency', { ...p, q: val }));
    }, 400);

    // Filter Handler
    const handleChangeFilterData = (e) => {
        const { name, value } = e.target;
        if (name) {
            if (name === 'q') {
                setQueryStr({ q: value });
                debounceUpdate(value);
            } else {
                navigate(mkRelativeUrl('/agency', { ...p, [name]: value }));
            }
        }
    };

    // For mobile view reset filter
    const onResetFilter = () => {
        navigate(mkRelativeUrl('/agency', { status: '', q: '' }));
    };

    // Pagination Handler
    const onChangePage = (pageNumber) => {
        navigate(mkRelativeUrl('/agency', { ...p, page: pageNumber }));
    };
    // Sorting Handler
    const handleSortingData = (column) => {
        navigate(
            mkRelativeUrl('/agency', {
                ...p,
                'order-by': column,
                sort: p.sort === 'desc' ? 'asc' : 'desc'
            })
        );
    };

    useEffect(() => {
        setParams(p);
    }, [p, setParams]);

    return (
        <div className="agency_list_page inner_page_cont overflow-y-scroll">
            {/* Page Header */}
            <LayoutProvider>
                <AgencyPageHeader
                    layout="default"
                    p={{ ...p, ...queryStr }}
                    handleChangeFilterData={handleChangeFilterData}
                />
                {/* Agency List Table */}
                <AgencyPageHeaderMobile
                    layout={['md', 'sm', 'xs', 'xxs']}
                    onResetFilter={onResetFilter}
                    onChangeHandler={handleChangeFilterData}
                    p={{ ...p, ...queryStr }}
                />
            </LayoutProvider>

            {/* Page Body - Table, No Data, Loading */}
            {/* Pagination */}
            <LayoutProvider>
                <AgencyPageTableWeb
                    layout="default"
                    data={agencyInfo?.list}
                    handleSortingData={handleSortingData}
                    sortBy={p.sort}
                    sortedColumn={p['order-by']}
                    pagination={agencyInfo?.pagination || {}}
                    onChangePage={onChangePage}
                />
                <AgencyPageTableMobile layout={['md', 'sm', 'xs', 'xxs']} data={agencyInfo?.list} />
            </LayoutProvider>

            {modalType === 'move-to-under-agency' && (
                <LibModal
                    title="Move Customer to Under Agency"
                    setOpen={closeModal}
                    className="md:!w-[550px]"
                >
                    <ModalCustomerMoveAgency />
                </LibModal>
            )}

            {modalType === 'create-agency-modal' && (
                <LibModal title="Create New Agency" setOpen={closeModal} className="md:!w-[550px]">
                    <ModalCreateAgency />
                </LibModal>
            )}
            {/* Create Agency Customer Modal */}
            {modalType === 'create-agency-customer-modal' && (
                <LibModal
                    title="Create New Agency Customer"
                    setOpen={closeModal}
                    className="md:!w-[550px]"
                >
                    <ModalCreateAgencyCustomer onReload={handleReload} />
                </LibModal>
            )}

            {/* Change Customer Currency Modal */}
            {modalType === 'CHANGE_CUSTOMER_CURRENCY_MODEL' && (
                <LibModal
                    title="Create New Agency Customer"
                    setOpen={closeModal}
                    className="md:!w-[450px]"
                >
                    <ModalChangeCustomerCurrency handleReload={handleReload} />
                </LibModal>
            )}

            {/* Update Agency Info Modal */}
            {modalType === 'UPDATE_AGENCY_INFO_LIST_MODAL' && (
                <LibModal title="Update Agency Info" setOpen={closeModal} className="md:!w-[450px]">
                    <ModalAgencyInfoUpdate onReload={handleReload} />
                </LibModal>
            )}

            {/* Change Agency Currency Modal */}
            {modalType === 'CHANGE_AGENCY_CURRENCY_MODAL' && (
                <LibModal
                    title="Change Agency Currency"
                    setOpen={closeModal}
                    className="md:!w-[450px]"
                >
                    <ModalAgencyChangeCurrency onReload={handleReload} />
                </LibModal>
            )}
        </div>
    );
}

function AgencyListPage() {
    // set page title
    PageTitle(`Customer List`);

    return (
        <AgencyProvider>
            <PageCont />
        </AgencyProvider>
    );
}

export default AgencyListPage;
